import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import {
  SORTING_ORDER,
  PURIFIER_EDIT_ALLOWED_USER_ROLES,
} from '../../../constants/index';
import moment from 'moment';
import { fetchPurifierOrdersLists } from '../actions/action';
import { getAreaList } from '../../ServiceOrders/ManageServiceOrders/actions/action';
import { Table, Row, Col, Spinner, Badge } from 'reactstrap';
import 'react-drawer/lib/react-drawer.css';
import DHPagination from '../../../components/Pagination';
import FilterSearchQueue from '../../../components/FilterSearch/FilterSearchQueue';
import './PurifierOrderList.css';
import PurifierServiceHistoryOffcanvas from './PurifierServiceHistoryOffcanvas';
import PurifierEditOffcanvas from './PurifierEditOffcanvas';

const ManagePurifierOrdersList = (props) => {
  const { stores, refreshList } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue, setLimitValue] = useUrlSearchParams({ limit: 10 });
  const [sortingValue, setSortingValue] = useUrlSearchParams({
    sort: '_date',
    order: SORTING_ORDER.ASC,
  });
  const { country = [] } = useSelector((state) => state.country);
  let currentCountry = localStorage.getItem('WEBSITE_COUNTRY');
  const [advancedFilter, setAdvancedFilter] = useState({});
  const [searchFilter, setSearchFilter] = useUrlSearchParams({ search: '' });
  const [statusFilter, setStatusFilter] = useUrlSearchParams({ status: 'All' });

  // Ensure the default query params are reflected in the URL on first load
  // (matches the canonical betdev URL shape).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      !params.has('status') ||
      !params.has('search') ||
      !params.has('page') ||
      !params.has('limit') ||
      !params.has('sort') ||
      !params.has('order')
    ) {
      setStatusFilter({ status: params.get('status') ?? 'All' });
      setSearchFilter({ search: params.get('search') ?? '' });
      setPaginationValue({ page: params.get('page') ?? 0 });
      setLimitValue({ limit: params.get('limit') ?? 10 });
      setSortingValue({
        sort: params.get('sort') ?? '_date',
        order: params.get('order') ?? SORTING_ORDER.ASC,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [emirate, setEmirate] = useState('');
  const [area, setArea] = useState('');
  const [isAmcFilter, setIsAmcFilter] = useState('');
  const [localRefresh, setLocalRefresh] = useState(0);

  const [type, setType] = useState('purfier');
  const [store, setStore] = useState('');
  const [storeType, setStoreType] = useState('all');
  const [allAreas, setAllAreas] = useState([]);

  const fetchpurifierOrdersList = useCallback(async (data) => {
    try {
      setDataLoading(true);
      await fetchPurifierOrdersLists({
        params: data,
        dispatch,
      });
    } catch (error) {
      setDataLoading(false);
      console.error('Error fetching orders:', error);
    } finally {
      setDataLoading(false);
    }
  }, []);

  const fetchAreaList = useCallback(async () => {
    try {
      const response = await getAreaList();
      // Handle both { data: [...] } and directly [...] formats
      const areas = Array.isArray(response) ? response : response?.data || [];
      setAllAreas(areas);
    } catch (error) {
      console.error('Error fetching area list:', error);
    }
  }, []);

  useEffect(() => {
    fetchAreaList();
  }, [fetchAreaList]);

  // Reset page to 0 when any filter changes, but only if filters actually changed
  useEffect(() => {
    setPaginationValue({ page: 0 });
  }, [
    searchFilter?.search,
    statusFilter?.status,
    JSON.stringify(advancedFilter),
    emirate,
    area,
    isAmcFilter,
  ]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchpurifierOrdersList({
        start: Number(paginationValue.page * limitValue.limit),
        limit: Number(limitValue.limit),
        sort: { value: sortingValue.sort, order: Number(sortingValue.order) },
        search_qry: searchFilter?.search,
        status: statusFilter?.status === 'All' ? '' : statusFilter?.status,
        is_amc:
          isAmcFilter === '' || isAmcFilter === 'All'
            ? ''
            : Number(isAmcFilter),
        advancedFilter,
        country: currentCountry,
        stores,
        emirate,
        area,
      });
    }, 300);

    return () => clearTimeout(delay);
  }, [
    paginationValue.page,
    limitValue,
    advancedFilter,
    searchFilter,
    statusFilter,
    stores,
    refreshList, // <-- add this dependency
    emirate,
    area,
    isAmcFilter,
    localRefresh,
  ]);

  const { purifierOrdersList = [], isLoading } = useSelector(
    (state) => state.complaints,
  );

  const [isDataLoading, setDataLoading] = useState(false);
  const { listOrders } = purifierOrdersList;
  const purifierOrderData = listOrders?.data;

  const areaOptions = Array.from(
    new Set(
      (allAreas?.length > 0 ? allAreas : [])
        .map((area) =>
          typeof area === 'string' ? area : area.name || area.area,
        )
        .filter((a) => a && a !== '-'),
    ),
  ).sort();

  let UserRoleID = '';
  let UserCountry = '';

  if (localStorage.getItem('authUser')) {
    const userObj = JSON.parse(localStorage.getItem('authUser'));
    if (userObj?.role_id !== undefined) {
      UserRoleID = userObj?.role_id;
      UserCountry = userObj?.country;
    }
  }
  const canEditPurifier = PURIFIER_EDIT_ALLOWED_USER_ROLES.includes(
    String(UserRoleID),
  );

  let pageIndex =
    Number(purifierOrderData?.length) > 0
      ? Number(paginationValue.page * limitValue.limit)
      : 0;

  const [viewMode, setViewMode] = useState(false);
  const [showHistoryOffcanvas, setShowHistoryOffcanvas] = useState(false);
  const [selectedLsOrderNo, setSelectedLsOrderNo] = useState(null);
  const [showEditOffcanvas, setShowEditOffcanvas] = useState(false);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);

  const toggleHistoryOffcanvas = () => setShowHistoryOffcanvas((prev) => !prev);
  const handleHistoryView = (lsOrderNo) => {
    setSelectedLsOrderNo(lsOrderNo);
    setShowHistoryOffcanvas(true);
  };
  const toggleEditOffcanvas = () => setShowEditOffcanvas((prev) => !prev);
  const handleEdit = (order) => {
    setSelectedOrderForEdit(order);
    setShowEditOffcanvas(true);
  };
  const handleEditSaved = () => {
    setLocalRefresh((n) => n + 1);
  };

  const customStyles = `
  .refundOrder .react-drawer-drawer {
    width: ${viewMode ? '50%' : ''} !important;
  }
`;

  return (
    <>
      <>
        <style>{customStyles}</style>
        <Row>
          <Col xl={12}>
            <FilterSearchQueue
              type={'purifier-orders'}
              setAdvancedFilter={setAdvancedFilter}
              setSearchFilter={setSearchFilter}
              setStatusFilter={setStatusFilter}
              setEmirate={setEmirate}
              setArea={setArea}
              setIsAmc={setIsAmcFilter}
              emirate={emirate}
              area={area}
              isAmc={isAmcFilter}
              areaOptions={areaOptions}
            />
            <div className=" dt-table">
              <div className="table-responsive">
                <Table className="OrderListTable ServiceOrderTable table mb-0">
                  <thead>
                    <tr>
                      <th width="250">Order Details #</th>
                      <th width="nowrap">Customer</th>
                      <th width="nowrap">Customer Address</th>
                      <th width="nowrap">Area Name</th>
                      <th nowrap="nowrap">Emirate</th>
                      <th nowrap="nowrap">Store</th>
                      <th nowrap="nowrap">Model No</th>
                      <th style={{ minWidth: '140px' }}>AMC Status</th>
                      <th nowrap="nowrap">Installation & Warranty</th>
                      {/* <th nowrap="nowrap">Created By</th> */}
                      <th nowrap="nowrap">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading || isDataLoading
                      ? [...Array(4)].map((_, index) => (
                          <tr key={`skeleton-${index}`}>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-12"></span>
                                <span className="placeholder col-6"></span>
                                <span
                                  className="placeholder"
                                  style={{ width: '25%' }}
                                ></span>
                                <span className="placeholder w-75"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow d-flex flex-column gap-1">
                                <span className="placeholder col-6"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                                <span
                                  className="placeholder"
                                  style={{ width: '25%' }}
                                ></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                                <span
                                  className="placeholder"
                                  style={{ width: '25%' }}
                                ></span>
                              </p>
                            </td>
                            <td>
                              <p className="placeholder-glow">
                                <span className="placeholder col-6"></span>
                                <span
                                  className="placeholder"
                                  style={{ width: '25%' }}
                                ></span>
                              </p>
                            </td>
                          </tr>
                        ))
                      : null}
                  </tbody>
                  {!(isLoading || isDataLoading) &&
                  purifierOrderData?.length > 0 ? (
                    <tbody>
                      {(purifierOrderData || []).map((purifierOrder, index) => {
                        pageIndex++;
                        let cdate = purifierOrder.created_at,
                          orderDt = moment(new Date(cdate)).format(
                            'Do MMM, YYYY',
                          ),
                          orderTime = moment(new Date(cdate)).format(
                            'h:mm:ss a',
                          );
                        return (
                          <tr key={purifierOrder?.ls_order_no || index}>
                            <td width="250">
                              <p className="fs-12 mb-0">
                                <b>{purifierOrder?.sku}</b>
                              </p>
                              <p className="fs-12 mb-0 mt-0">
                                {orderDt} {orderTime}
                              </p>
                              {purifierOrder?.ls_order_no && (
                                <p className="fs-10 mb-0 mt-1">
                                  <b>LS Order No :</b>{' '}
                                  {purifierOrder?.ls_order_no}
                                </p>
                              )}
                              {purifierOrder?.service_order_no && (
                                <p className="fs-10 mb-0 mt-1">
                                  Service Order No :
                                  <b>{purifierOrder?.service_order_no}</b>
                                </p>
                              )}
                            </td>

                            <td>
                              <span className="fs-12">
                                {purifierOrder?.customer_name}
                              </span>
                              <p className="fs-12">
                                {purifierOrder?.mobile}
                                {purifierOrder?.alternate_mobile && (
                                  <>
                                    <br />
                                    <span className="text-muted">
                                      Alt: {purifierOrder.alternate_mobile}
                                    </span>
                                  </>
                                )}
                                <br />
                                {purifierOrder?.email}
                              </p>
                            </td>
                            <td>{purifierOrder?.address1}</td>
                            <td>
                              {purifierOrder?.area ? purifierOrder?.area : '-'}
                            </td>
                            <td>{purifierOrder?.emirate}</td>
                            <td>{purifierOrder?.store}</td>
                            <td>
                              <div>{purifierOrder?.model_no}</div>
                              <small className="text-muted">
                                <b>Serial No:</b>{' '}
                                {purifierOrder?.serial_no || '-'}
                              </small>
                            </td>
                            <td>
                              {(() => {
                                const isAmc = purifierOrder?.is_amc;
                                let badgeColor = 'bg-soft-light text-dark';
                                let iconClass = 'mdi mdi-help-circle-outline';
                                let statusText = 'Unknown';

                                if (isAmc === true) {
                                  badgeColor = 'bg-soft-success text-success';
                                  iconClass = 'mdi mdi-check-decagram';
                                  statusText = 'Active AMC';
                                } else if (isAmc === false) {
                                  badgeColor = 'bg-soft-danger text-danger';
                                  iconClass = 'mdi mdi-close-octagon-outline';
                                  statusText = 'Non-AMC';
                                }

                                return (
                                  <div className="d-flex flex-column align-items-start gap-1">
                                    <div
                                      className={`badge fs-11 px-2 py-1 rounded-pill ${badgeColor} d-inline-flex align-items-center gap-1 shadow-sm border-0`}
                                    >
                                      <i className={`${iconClass} fs-13`}></i>
                                      <span className="fw-semibold">
                                        {statusText}
                                      </span>
                                    </div>

                                    {isAmc === true &&
                                      purifierOrder?.amc_start_date && (
                                        <div
                                          className="p-1 px-2 rounded-2 border border-dashed border-success border-opacity-25 bg-soft-success bg-opacity-10 mt-1 d-flex flex-column gap-1"
                                          style={{ minWidth: 'max-content' }}
                                        >
                                          <div
                                            className="d-flex align-items-center gap-1 text-success"
                                            style={{ fontSize: '9px' }}
                                          >
                                            <i className="mdi mdi-calendar-clock fw-bold fs-12"></i>
                                            <span className="fw-bold text-uppercase tracking-wider">
                                              AMC Period:
                                            </span>
                                          </div>
                                          <div
                                            className="d-flex align-items-center gap-1 text-dark fw-bold ps-1"
                                            style={{ fontSize: '10px' }}
                                          >
                                            <span>
                                              {moment(
                                                new Date(
                                                  purifierOrder?.amc_start_date,
                                                ),
                                              ).format('DD MMM YY')}
                                            </span>
                                            <span className="text-muted opacity-50 px-1">
                                              to
                                            </span>
                                            <span>
                                              {moment(
                                                new Date(
                                                  purifierOrder?.amc_end_date,
                                                ),
                                              ).format('DD MMM YY')}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1">
                                {purifierOrder?.installation_date && (
                                  <div
                                    className="d-flex align-items-center gap-2 ps-1"
                                    style={{ minWidth: 'max-content' }}
                                  >
                                    <div
                                      className="d-flex align-items-center gap-1 text-success"
                                      style={{ fontSize: '9px' }}
                                    >
                                      <i className="mdi mdi-wrench-outline fw-bold fs-12"></i>
                                      <span className="fw-bold text-uppercase tracking-wider">
                                        Installed On:
                                      </span>
                                    </div>
                                    <span className="text-dark fw-bold fs-10">
                                      {moment(
                                        new Date(
                                          purifierOrder?.installation_date,
                                        ),
                                      ).format('DD MMM YY')}
                                    </span>
                                  </div>
                                )}
                                {purifierOrder?.warranty_end_date && (
                                  <div
                                    className="d-flex align-items-center gap-2 ps-1"
                                    style={{ minWidth: 'max-content' }}
                                  >
                                    <div
                                      className="d-flex align-items-center gap-1 text-info"
                                      style={{ fontSize: '9px' }}
                                    >
                                      <i className="mdi mdi-shield-link-variant-outline fw-bold fs-12"></i>
                                      <span className="fw-bold text-uppercase tracking-wider">
                                        Warranty Until:
                                      </span>
                                    </div>
                                    <span className="text-dark fw-bold fs-10">
                                      {moment(
                                        new Date(
                                          purifierOrder?.warranty_end_date,
                                        ),
                                      ).format('DD MMM YY')}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="action-flex-container">
                                <span
                                  className="btn btn-sm btn-outline-warning waves-effect waves-light"
                                  title="View Service History Info"
                                  onClick={() => {
                                    if (purifierOrder?.ls_order_no) {
                                      handleHistoryView(
                                        purifierOrder?.ls_order_no,
                                      );
                                    }
                                  }}
                                >
                                  <i className="mdi mdi-eye-outline"></i>
                                </span>
                                {canEditPurifier && (
                                  <span
                                    className="btn btn-sm btn-outline-primary waves-effect waves-light"
                                    title="Edit Purifier Order"
                                    onClick={() => handleEdit(purifierOrder)}
                                  >
                                    <i className="mdi mdi-pencil-outline"></i>
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={11} className="text-center p-3">
                          No Records found
                        </td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              </div>
              {purifierOrderData?.length > 0 ? (
                <DHPagination
                  totalPages={Math.ceil(
                    (listOrders?.recordsTotal || 0) / (limitValue?.limit || 10),
                  )}
                  currentPage={paginationValue.page}
                  setPaginationValue={setPaginationValue}
                  setLimitValue={setLimitValue}
                />
              ) : null}
            </div>
          </Col>
        </Row>
        <PurifierServiceHistoryOffcanvas
          isOpen={showHistoryOffcanvas}
          toggle={toggleHistoryOffcanvas}
          lsOrderNo={selectedLsOrderNo}
        />
        <PurifierEditOffcanvas
          isOpen={showEditOffcanvas}
          toggle={toggleEditOffcanvas}
          order={selectedOrderForEdit}
          onSaved={handleEditSaved}
        />
      </>
    </>
  );
};

export default ManagePurifierOrdersList;
